import { forwardRef } from "react";

import { twMerge } from "@/utils/twMerge";

import type { PolymorphicProps } from "@/components/expression/utils/NodeLayout";
import type { ComponentProps, PropsWithChildren } from "react";

export type LabelPlacement = "inside" | "outside";

export type LabelProps<T extends React.ElementType> = PolymorphicProps<T> & {
  label?: string;
  className?: string;
  labelClassName?: string;
  labelPlacement?: LabelPlacement;
  enableFocus?: boolean;
};

export function NodeFieldLabel<T extends React.ElementType = "label">({
  as,
  label,
  className,
  labelClassName,
  children,
  enableFocus = true,
  labelPlacement = "inside",
  ...props
}: PropsWithChildren<LabelProps<T>>) {
  const Component = as || "label";
  const renderLabelInside = labelPlacement === "inside";

  const containerClassNames = twMerge(
    "flex items-center bg-document-bg relative rounded-6 cursor-text",
    enableFocus &&
      "focus-within:outline-solid focus-within:outline-2 focus-within:outline-[-webkit-focus-ring-color] focus-within:z-20",
    className,
  );

  return (
    <Component
      aria-label={label}
      className={twMerge("w-full min-w-0 relative", renderLabelInside && containerClassNames)}
      {...props}
    >
      {label && (
        <span
          className={twMerge(
            "font-mono text-[11px] leading-0 cursor-text select-none uppercase tracking-tight text-gray-500 flex-shrink-0 pl-2 -mr-[5px]",
            !renderLabelInside && "pl-1 text-xxxs",
            labelClassName,
          )}
        >
          {label}
          {renderLabelInside && ":"}
        </span>
      )}
      {renderLabelInside && children}
      {!renderLabelInside && <div className={containerClassNames}>{children}</div>}
    </Component>
  );
}

export type Props = ComponentProps<"input"> & {
  labelProps?: LabelProps<"label">;
  action?: React.ReactNode;
};

export const NodeTextField = forwardRef<HTMLInputElement, Props>(
  ({ id, className, labelProps = {}, action, children, ...props }, ref) => {
    return (
      <NodeFieldLabel {...labelProps} htmlFor={id}>
        <input
          ref={ref}
          id={id}
          type="text"
          data-1p-ignore
          className={twMerge(
            "text-sm w-full py-1 px-2 outline-none bg-transparent",
            action && "pr-6 [&:focus+div]:hidden",
            className,
          )}
          {...props}
        />
        {action && (
          <div className="absolute flex items-center max-w-[50%] right-0 top-0 bottom-0 p-0.5 pl-0">
            {action}
          </div>
        )}
        {children}
      </NodeFieldLabel>
    );
  },
);
